import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { SingleValue } from "react-select";
import { Loader2 } from "lucide-react";
import { DateFiltersProps, LojaOption } from "./types";
import LojaFilter from "./components/LojaFilter";
import DateInput from "./components/DateInput";

const APPLY_FEEDBACK_MS = 600;

const DateFilters: React.FC<DateFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyFilter,
  lojas = [],
  selectedLojaId = null,
  onLojaChange,
  title = "Filtrar Resumo",
  appliedSummary = null,
}) => {
  const [isApplying, setIsApplying] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const handleApplyClick = useCallback(() => {
    if (isApplying) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsApplying(true);
    onApplyFilter();
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setIsApplying(false);
    }, APPLY_FEEDBACK_MS);
  }, [onApplyFilter, isApplying]);

  const lojaOptions = useMemo(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );

  const selectedLojaOption = useMemo(
    () =>
      selectedLojaId
        ? lojaOptions.find((opt) => opt.value === selectedLojaId) || null
        : null,
    [selectedLojaId, lojaOptions]
  );

  const handleLojaChange = (option: SingleValue<LojaOption>) => {
    onLojaChange?.(option?.value ?? null);
  };

  const handleEndDateChange = (date: string) => {
    if (date < startDate) {
      onStartDateChange(date);
    }
    onEndDateChange(date);
  };

  const showLojaFilter = lojas.length > 0 && onLojaChange;

  return (
    <>
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-neutral-800">{title}</h2>
      </div>
      <div
        className={`rounded-lg border border-neutral-200 bg-white p-4 shadow-sm ${appliedSummary ? "mb-3" : "mb-6"}`}
      >
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
          {showLojaFilter && (
            <LojaFilter
              lojaOptions={lojaOptions}
              selectedLojaOption={selectedLojaOption}
              onLojaChange={handleLojaChange}
            />
          )}
          <DateInput
            id="start-date"
            label="Data Início"
            value={startDate}
            onChange={onStartDateChange}
          />
          <DateInput
            id="end-date"
            label="Data Fim"
            value={endDate}
            onChange={handleEndDateChange}
          />
          <button
            type="button"
            onClick={handleApplyClick}
            disabled={isApplying}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white transition duration-200 hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-90 sm:w-auto sm:px-4 sm:text-sm"
          >
            {isApplying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                <span>Aplicando...</span>
              </>
            ) : (
              "Aplicar Filtro"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default React.memo(DateFilters);
